import { useCallback, useEffect, useState } from "react";
import {
  Briefcase,
  Building2,
  Calendar,
  DollarSign,
  Eye,
  Mail,
  MapPin,
  Phone,
  Search,
  Users,
  X,
} from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";

const PAGE_SIZE = 10;

/* Inquiries submitted from the public /seller landing page and from the
   external marketing site, which posts to the same endpoint. Rows are read-only
   apart from the workflow status and the internal note. */

interface SellerContact {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  business_type: string;
  location: string;
  business_age: string;
  employees: string;
  revenue: string;
  ebitda: string | null;
  reason: string;
  message: string | null;
  status: string;
  admin_notes: string | null;
  source: string;
  created_at: string;
  updated_at: string;
}

interface ContactPage {
  items: SellerContact[];
  total: number;
  page: number;
  page_size: number;
}

interface Stats {
  total: number;
  new: number;
  contacted: number;
  qualified: number;
}

const STATUSES = ["new", "contacted", "qualified", "disqualified", "closed"] as const;

// Semantic status colors, kept translucent so they sit correctly on the dark theme.
const STATUS_STYLE: Record<string, string> = {
  new: "border-primary/40 bg-primary/10 text-primary",
  contacted: "border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400",
  qualified: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  disqualified: "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
  closed: "border-border/70 bg-muted/40 text-muted-foreground",
};

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize whitespace-nowrap ${
        STATUS_STYLE[status] ?? STATUS_STYLE.closed
      }`}
    >
      {status}
    </span>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: typeof Mail; label: string; value: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="mt-1 text-sm text-foreground/90 break-words">{value}</p>
    </div>
  );
}

export default function AdminSellerContactsPage() {
  const [data, setData] = useState<ContactPage | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<SellerContact | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const loadStats = useCallback(() => {
    api.get<Stats>("/seller-contacts/admin/stats").then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (appliedSearch.trim()) params.set("search", appliedSearch.trim());
    if (status !== "all") params.set("status", status);
    api
      .get<ContactPage>(`/seller-contacts/admin?${params}`)
      .then(setData)
      .catch(() => toast.error("Failed to load seller inquiries."))
      .finally(() => setLoading(false));
  }, [page, appliedSearch, status, toast]);

  useEffect(loadStats, [loadStats]);

  // Filters change the result set, so restart at page 1.
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, status]);

  const resultCount = data?.total ?? 0;
  const displayed = data?.items ?? [];

  function openDetail(c: SellerContact) {
    setSelected(c);
    setNotes(c.admin_notes ?? "");
  }

  async function save(next: { status?: string; admin_notes?: string }) {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await api.patch<SellerContact>(`/seller-contacts/admin/${selected.id}`, next);
      setSelected(updated);
      setNotes(updated.admin_notes ?? "");
      setData((d) => (d ? { ...d, items: d.items.map((i) => (i.id === updated.id ? updated : i)) } : d));
      loadStats();
      toast.success("Inquiry updated.");
    } catch {
      toast.error("Failed to update the inquiry.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Seller Leads"
        description="Confidential inquiries submitted from the seller landing page and the marketing site. Work the queue by moving each one through its status."
        aside={
          <div>
            <h2 className="text-lg font-bold text-foreground">Inquiry snapshot</h2>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              {[
                { label: "Total", value: stats?.total ?? 0 },
                { label: "New", value: stats?.new ?? 0 },
                { label: "Contacted", value: stats?.contacted ?? 0 },
                { label: "Qualified", value: stats?.qualified ?? 0 },
              ].map((t) => (
                <div key={t.label} className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {t.label}
                  </div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{t.value}</div>
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* Filters */}
      <PagePanel>
        <div className="mb-4">
          <h2 className="text-base font-bold text-foreground">Search inquiries</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Find a seller by name, email, phone, business type, or location.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-10 pl-9 text-sm"
                placeholder="Search name, email, phone, business, location…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setAppliedSearch(search);
                }}
              />
            </div>
          </div>
          <div className="min-w-[150px]">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-10 w-full rounded-2xl border border-border/80 bg-card/85 px-3 text-sm capitalize focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <Button type="button" onClick={() => setAppliedSearch(search)} className="h-10 gap-2 rounded-full px-5">
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
          {(search || appliedSearch || status !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setAppliedSearch("");
                setStatus("all");
              }}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </PagePanel>

      {/* Table */}
      <PagePanel>
        <PanelHeading
          title="All Inquiries"
          description={
            data ? `${resultCount} inquir${resultCount !== 1 ? "ies" : "y"} ${appliedSearch || status !== "all" ? "found" : "total"}` : undefined
          }
        />

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : displayed.length === 0 ? (
          <EmptyPanel
            icon={Building2}
            title="No seller inquiries yet"
            description="Submissions from the /seller landing page and the marketing site will appear here."
          />
        ) : (
          <div>
            <div className="-mx-5 overflow-x-auto sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/30">
                    <th className="py-3 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pl-6">
                      Seller
                    </th>
                    <th className="hidden px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell">
                      Business
                    </th>
                    <th className="hidden px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:table-cell">
                      Revenue
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Status
                    </th>
                    <th className="hidden whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">
                      Submitted
                    </th>
                    <th className="py-3 pl-3 pr-5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pr-6">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {displayed.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => openDetail(c)}
                      className="group cursor-pointer align-top transition-colors hover:bg-muted/25"
                    >
                      <td className="min-w-[170px] py-3.5 pl-5 pr-3 sm:pl-6">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                            {c.full_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">{c.full_name}</p>
                            <p className="truncate text-xs text-muted-foreground">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-3 py-3.5 sm:table-cell">
                        <p className="text-foreground/90">{c.business_type}</p>
                        <p className="text-xs text-muted-foreground">{c.location}</p>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-3.5 text-foreground/90 lg:table-cell">
                        {c.revenue}
                      </td>
                      <td className="px-3 py-3.5">
                        <StatusChip status={c.status} />
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-3.5 text-xs text-muted-foreground md:table-cell">
                        {fmtDateTime(c.created_at)}
                      </td>
                      <td className="py-3.5 pl-3 pr-5 text-right sm:pr-6">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(c);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalElements={resultCount}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-1 px-5 sm:px-6"
            />
          </div>
        )}
      </PagePanel>

      {/* ── Inquiry detail modal ── */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-2xl">
            {/* Header */}
            <div className="shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-sm font-bold text-white">
                    {selected.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-bold leading-snug text-white">{selected.full_name}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-white/60">
                      <Mail className="h-3 w-3 shrink-0" />
                      {selected.email}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {fmtDateTime(selected.created_at)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  Source:
                  <span className="rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 font-medium text-foreground">
                    {selected.source}
                  </span>
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DetailRow icon={Phone} label="Phone" value={selected.phone} />
                <DetailRow icon={Briefcase} label="Business type" value={selected.business_type} />
                <DetailRow icon={MapPin} label="Location" value={selected.location} />
                <DetailRow icon={Calendar} label="Years in business" value={selected.business_age} />
                <DetailRow icon={Users} label="Employees" value={selected.employees} />
                <DetailRow icon={DollarSign} label="Annual revenue" value={selected.revenue} />
                <DetailRow icon={DollarSign} label="EBITDA" value={selected.ebitda ?? "Not provided"} />
                <DetailRow icon={Building2} label="Reason for sale" value={selected.reason} />
              </div>

              {selected.message && (
                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Message</p>
                  <div className="whitespace-pre-wrap rounded-xl border border-border/60 bg-muted/10 px-4 py-3.5 text-sm leading-relaxed text-foreground/90">
                    {selected.message}
                  </div>
                </div>
              )}

              {/* Workflow */}
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={saving || selected.status === s}
                      onClick={() => save({ status: s })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize transition-all disabled:cursor-default ${
                        selected.status === s
                          ? STATUS_STYLE[s]
                          : "border-border/70 bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Internal notes
                </p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  placeholder="Notes for the team — never shown to the seller."
                  className="w-full rounded-xl border border-border/80 bg-card/85 px-4 py-3 text-sm leading-relaxed transition-all placeholder:text-muted-foreground/70 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={saving || notes === (selected.admin_notes ?? "")}
                  onClick={() => save({ admin_notes: notes })}
                  className="mt-2"
                >
                  {saving ? "Saving…" : "Save notes"}
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-border px-6 py-4">
              <div className="flex flex-wrap gap-2.5">
                <a href={`mailto:${selected.email}`} className="flex-1">
                  <Button type="button" className="w-full gap-2">
                    <Mail className="h-4 w-4" />
                    Email seller
                  </Button>
                </a>
                <a href={`tel:${selected.phone}`} className="flex-1">
                  <Button type="button" variant="outline" className="w-full gap-2">
                    <Phone className="h-4 w-4" />
                    Call seller
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
